/**
 * Shared inventory helpers — status badges, transaction type colors/labels.
 * Extracted from Dashboard.tsx so LotDetailModal, FinishedProductHistoryModal,
 * and PackingMaterialsTab can import them directly.
 */

import {
  lotDisplayType,
  lotLifecycle,
  type LotDisplayType,
} from '@tnb/lot-status';

export { lotDisplayType, lotLifecycle };
export type { LotDisplayType };

/** WCO-3282 — canonical FP-NI (finish_product_ni) AMBER styling SSOT (matches the FP-NI section
 *  header icon; decision 2026-08-26: FP-NI badges follow the amber section icon). */
export const FP_NI_BADGE_CLASSES =
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';

/** Card row styling for mobile waste summary surfaces */
export const FP_NI_SUMMARY_CARD_CLASSES = {
  container: 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg',
  label: 'text-amber-900 dark:text-amber-100',
  value: 'text-amber-700 dark:text-amber-300',
} as const;

/** Text color for EoD waste table rows */
export const FP_NI_WASTE_ROW_CLASSES = {
  color: 'text-amber-600 dark:text-amber-400',
} as const;

/** Inline tag styling (e.g. production output scan extras) */
export const FP_NI_TAG_CLASSES = 'text-amber-600 dark:text-amber-400';

/** Inventory lot status → Tailwind badge classes */
export const getStatusBadge = (status: string | null | undefined, approvalStatus?: string): string => {
  // WCO-3469: boxSearch.ts excludes nullable pi.status via NOT IN ('voided','cancelled'), so NULL
  // does not reach the UI today — accidental protection, not a deliberate null-safety contract.
  const normalizedStatus = status ?? '';
  if (normalizedStatus === 'on_hold') {
    return approvalStatus === 'approved'
      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  }
  if (normalizedStatus.startsWith('finish_product_ni')) {
    return FP_NI_BADGE_CLASSES;
  }
  const statusColors: Record<string, string> = {
    available: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    reserved: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    depleted: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    packed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    voided: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    remainder: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
    by_product: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    by_product_m: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    by_product_p: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
  };
  return statusColors[normalizedStatus] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

/** Lifecycle status → human-readable display name (for STATUS column after lotLifecycle derivation). */
export const getLifecycleDisplayName = (
  lifecycle: string | null | undefined,
  t: (key: string) => string,
  approvalStatus?: string,
): string => {
  const normalizedLifecycle = lifecycle ?? '';
  if (normalizedLifecycle === 'on_hold') {
    return approvalStatus === 'approved'
      ? (t('inventory.onHoldApproved') || 'On hold (approved)')
      : (t('inventory.onHoldPending') || 'On hold (pending)');
  }
  const statusNames: Record<string, string> = {
    available: t('inventory.statusAvailable') || 'Available',
    reserved: t('inventory.statusReserved') || 'Reserved',
    consumed: t('inventory.statusConsumed') || 'Consumed',
    depleted: t('inventory.statusDepleted') || 'Depleted',
    packed: t('inventory.statusPacked') || 'Packed',
    shipped: t('inventory.statusShipped') || 'Shipped',
    voided: t('inventory.statusVoided') || 'Voided',
    rejected: t('inventory.statusRejected') || 'Rejected',
  };
  return statusNames[normalizedLifecycle]
    || normalizedLifecycle.charAt(0).toUpperCase() + normalizedLifecycle.slice(1).replace(/_/g, ' ');
};

/** Presentation lot type → Tailwind badge classes.
 *  WCO-3270: a NOT-INTACT remainder (`isNiRemnant`) is a distinct raw-material form (loose pieces,
 *  no case count), so it gets its own orange tint; raw + intact remainders share the slate "Intact"
 *  badge. */
export const getLotTypeBadge = (displayType: LotDisplayType, isNiRemnant?: boolean | null): string => {
  if (displayType === 'remainder' && isNiRemnant) {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
  }
  const typeColors: Record<LotDisplayType, string> = {
    raw: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
    remainder: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
    fp_ni: FP_NI_BADGE_CLASSES,
    by_product_p: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
    by_product_m: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  };
  return typeColors[displayType];
};

/** Presentation lot type → human-readable display name.
 *  WCO-3270: inside the "Raw Material Lots" tab everything is raw material, so the TYPE column
 *  only conveys the FORM: raw and INTACT remainders render "Intact"; a NOT-INTACT remainder
 *  (`isNiRemnant`) renders "Not Intact" (loose pieces, no case count). ("Raw" was redundant here.) */
export const getLotTypeDisplayName = (
  displayType: LotDisplayType,
  t: (key: string) => string,
  isNiRemnant?: boolean | null,
): string => {
  if (displayType === 'remainder' && isNiRemnant) {
    return t('inventory.lotTypeRemainderNi') || 'Not Intact';
  }
  const typeKeys: Record<LotDisplayType, string> = {
    raw: 'inventory.lotTypeRaw',
    remainder: 'inventory.lotTypeRemainder',
    fp_ni: 'inventory.lotTypeFpNi',
    by_product_p: 'inventory.lotTypeByProductP',
    by_product_m: 'inventory.lotTypeByProductM',
  };
  return t(typeKeys[displayType]) || displayType;
};

/** Transaction type → Tailwind badge classes (pure, no i18n) */
export const TX_TYPE_COLORS: Record<string, string> = {
  session_start: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  session_cancel: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  session_complete: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  case_packed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  case_cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  case_voided: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  case_pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  waste_recorded: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  manual_adjustment: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  oracle_sync: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  material_issue: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  lot_received: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  lot_rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  lot_reserved: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  by_product_received: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  by_product_marketable_received: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  by_product_production_received: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-400',
  remainder_received: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  remainder_intact_received: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  remainder_not_intact_received: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  by_product_marketable_printed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  by_product_case_printed: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  finish_product_not_intact_reserved: FP_NI_BADGE_CLASSES,
  finish_product_not_intact_received: FP_NI_BADGE_CLASSES,
};

/** Build transaction type → label map (requires i18n `t` function) */
export const getTxTypeLabels = (t: (key: string) => string): Record<string, string> => ({
  session_start: t('inventory.transactionSessionStart'),
  session_cancel: t('inventory.transactionSessionCancel'),
  session_complete: t('inventory.transactionSessionComplete'),
  case_packed: t('inventory.transactionCasePacked'),
  case_cancelled: t('inventory.transactionCaseCancelled'),
  case_voided: t('inventory.transactionCaseVoided'),
  case_pending: t('inventory.transactionCasePending'),
  waste_recorded: t('inventory.transactionWasteRecorded'),
  manual_adjustment: t('inventory.transactionManualAdjustment'),
  oracle_sync: t('inventory.transactionOracleSync'),
  material_issue: t('inventory.transactionMaterialIssue'),
  lot_received: t('inventory.transactionLotReceived'),
  lot_rejected: t('inventory.transactionLotRejected'),
  lot_reserved: t('inventory.transactionLotReserved'),
  by_product_received: t('inventory.transactionByProductReceived'),
  by_product_marketable_received: t('inventory.transactionByProductMarketableReceived'),
  by_product_production_received: t('inventory.transactionByProductProductionReceived'),
  remainder_received: t('inventory.transactionRemainderReceived'),
  remainder_intact_received: t('inventory.transactionRemainderIntactReceived'),
  remainder_not_intact_received: t('inventory.transactionRemainderNotIntactReceived'),
  by_product_marketable_printed: t('inventory.transactionByProductMarketablePrinted'),
  by_product_case_printed: t('inventory.transactionByProductCasePrinted'),
  finish_product_not_intact_reserved: t('inventory.transactionFinishProductNotIntactReserved'),
  finish_product_not_intact_received: t('inventory.transactionFinishProductNotIntactReceived'),
});
